import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import LogoIcon from '../components/LogoIcon';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const isDark = mode === 'dark';

  const bg     = isDark ? '#0A0A0A' : '#F5F5F5';
  const card   = isDark ? '#111111' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888888' : '#666666';
  const textM  = isDark ? '#555555' : '#AAAAAA';

  return (
    <Box className="page-transition" sx={{
      minHeight: '100vh',
      bgcolor: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: 380 }}>

        {/* Brand mark */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 8 }}>
          <Tooltip title="Home" placement="right" arrow>
            <IconButton
              onClick={() => navigate('/home')}
              size="small"
              sx={{
                mr: 0.5, color: textM,
                '&:hover': { color: textP, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
              }}
            >
              <HomeOutlined sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          <LogoIcon size={20} letterColor={isDark ? '#EFEFEF' : '#FF1A50'} />
          <Typography sx={{
            fontWeight: 700, fontSize: '0.875rem',
            color: textP, letterSpacing: '-0.02em',
          }}>
            LLM Wrapper
          </Typography>
          <Box sx={{
            ml: 0.5, px: 1, py: 0.25, borderRadius: '4px',
            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            border: `1px solid ${border}`,
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: textM, letterSpacing: '0.04em' }}>
              ADMIN
            </Typography>
          </Box>
        </Box>

        {/* Heading */}
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700,
          color: textP, letterSpacing: '-0.03em',
          lineHeight: 1.2, mb: 1,
        }}>
          Sign in
        </Typography>
        <Typography sx={{
          fontSize: '0.875rem', color: textS,
          mb: 5, lineHeight: 1.6,
        }}>
          Use your{' '}
          <Box component="span" sx={{ color: textP, fontWeight: 500 }}>
            @testingxperts.com
          </Box>{' '}
          Microsoft account to access the admin console.
        </Typography>

        {/* Card */}
        <Box sx={{
          bgcolor: card,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: 3,
        }}>
          {/* Microsoft SSO button */}
          <Button
            fullWidth
            onClick={login}
            sx={{
              py: 1.25,
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'flex',
              gap: 1.5,
              bgcolor: isDark ? '#EFEFEF' : '#0A0A0A',
              color: isDark ? '#0A0A0A' : '#EFEFEF',
              borderRadius: '7px',
              border: 'none',
              letterSpacing: '-0.01em',
              '&:hover': {
                bgcolor: isDark ? '#FFFFFF' : '#1A1A1A',
              },
              transition: 'background 0.15s ease',
              textTransform: 'none',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
              <rect x="0"  y="0"  width="10" height="10" fill="#F25022"/>
              <rect x="11" y="0"  width="10" height="10" fill="#7FBA00"/>
              <rect x="0"  y="11" width="10" height="10" fill="#00A4EF"/>
              <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
            </svg>
            Continue with Microsoft
          </Button>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: border }} />
            <Typography sx={{ fontSize: '0.72rem', color: textM, fontWeight: 500, letterSpacing: '0.06em' }}>
              SECURED BY AZURE AD
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: border }} />
          </Box>

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {['SSO Enabled', 'MFA Protected', 'Role-Based Access'].map(tag => (
              <Box key={tag} sx={{
                px: 1.25, py: 0.5, borderRadius: '5px',
                border: `1px solid ${border}`,
                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              }}>
                <Typography sx={{ fontSize: '0.68rem', color: textM, fontWeight: 500 }}>
                  {tag}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.72rem', color: textM, lineHeight: 1.6 }}>
            Access restricted to authorized TestingXperts personnel.
          </Typography>
          <Box
            onClick={() => navigate('/docs')}
            sx={{
              fontSize: '0.72rem', color: textM, cursor: 'pointer', fontWeight: 500,
              '&:hover': { color: '#FF1A50' }, transition: 'color 0.15s', whiteSpace: 'nowrap', ml: 2,
            }}
          >
            API Docs →
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
